import { useEffect } from "react";
import { useNotifications } from "@novu/react";
import type { Notification } from "@novu/react";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";

import { AllstarModalState } from "MVVM/State/modals";
import { WelcomeSeriesGate } from "MVVM/State/welcomeSeries";
import { ModalType } from "MVVM/@types";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { useIsMobileApp } from "MVVM/Hooks/useIsMobileApp";
import { analytics } from "MVVM/Analytics";

// Contract with sonder (workflows/welcome-series.ts): welcome in-app messages
// carry data { kind, step, display, expiresAt, imageUrl? }. display "modal"
// ones pop the companion modal until expiresAt (end of the enrollment window);
// "feed" ones only appear in the bell.
const WELCOME_KIND = "welcome-series";
const SESSION_KEY = "welcome-series-modal-shown";

const EXCLUDED_PATHS = [
  "/oauth",
  "/iframe",
  "/authenticate",
  "/register",
  "/login",
];

export const isEligible = (notification: Notification) => {
  const data = notification.data as
    | { kind?: string; display?: string; expiresAt?: string }
    | undefined;
  if (notification.isRead || data?.kind !== WELCOME_KIND) return false;
  if (data.display !== "modal") return false;
  if (data.expiresAt && new Date(data.expiresAt).getTime() < Date.now())
    return false;
  return true;
};

/**
 * Watches the Novu feed for unread welcome-series messages flagged for modal
 * display and opens the companion modal through the standard AllstarModalState
 * flow — max one per session, deferring to any modal already open. Activating
 * the WelcomeSeriesGate suppresses FirstLandTakeover marketing promos so a
 * brand-new user gets onboarding before marketing, never both stacked.
 */
export const WelcomeSeriesOrchestrator = () => {
  const { notifications } = useNotifications();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const [, setWelcomeSeriesGate] = useRecoilState(WelcomeSeriesGate);
  const location = useLocation();
  const { isMobileApp } = useIsMobileApp();

  const candidate = notifications?.find(isEligible);

  // Gate marketing takeovers as soon as we know a welcome modal is coming,
  // even if another modal is briefly blocking us from opening it.
  useEffect(() => {
    if (candidate && !sessionStorage.getItem(SESSION_KEY))
      setWelcomeSeriesGate({ active: true });
  }, [candidate, setWelcomeSeriesGate]);

  useEffect(() => {
    if (!candidate) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (allstarModalState.isOpen !== ModalType.None) return;
    if (isMobileApp) return;
    if (getQueryParam("hide") === "popover") return;
    if (EXCLUDED_PATHS.some((path) => location.pathname.includes(path))) return;

    const data = candidate.data as { step?: string; imageUrl?: string };
    sessionStorage.setItem(SESSION_KEY, "true");

    analytics.track("Promotion - Impression", {
      type: "welcomeSeriesModal",
      promotionName: `welcome-${data.step}`,
    });

    setAllstarModalState({
      isOpen: ModalType.WelcomeSeries,
      data: {
        title: candidate.subject,
        body: candidate.body,
        ctaLabel: candidate.primaryAction?.label,
        ctaUrl:
          candidate.primaryAction?.redirect?.url ?? candidate.redirect?.url,
        step: data.step,
        imageUrl: data.imageUrl,
      },
      functions: {
        markRead: () => candidate.read(),
      },
    });
  }, [
    candidate,
    allstarModalState.isOpen,
    isMobileApp,
    location.pathname,
    setAllstarModalState,
  ]);

  return <></>;
};
